import React, { useEffect, useState } from 'react';
import { auth } from '../../../../firebase/config';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import { FiPlus, FiSearch, FiEye, FiCheck, FiX, FiMail, FiDownload } from 'react-icons/fi';
import Icon from '../../../../components/Icon';
import AddChangeOfVendorModal from '../components/AddChangeOfVendorModal/index';
import Modal from '../../../../components/Modal';
import { fetchChangeOfVendorsData, updateChangeOfVendorStatus, updateDeploymentDate, fetchChangeOfVendorBankId, deleteChangeOfVendor, type ChangeOfVendor } from '../service/changeOfVendorService';
import { useAppSelector } from '../../../../store';
import { getDocs, collection, doc, getDoc } from "firebase/firestore";
import { db } from '../../../../firebase/config';
import { fetchCompaniesData } from '../../../companies/service/companiesService';
import type { Company } from '../../../companies/service/companiesService';
import { sendEmailsWithErrorHandling, validateEmails } from '../../../../api/emailService';
import TextField from '../../../../components/TextField';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

export default function ChangeOfVendors() {
    const [changeOfVendors, setChangeOfVendors] = useState<ChangeOfVendor[]>([]);
    const [filtered, setFiltered] = useState<ChangeOfVendor[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [selectedChangeOfVendor, setSelectedChangeOfVendor] = useState<ChangeOfVendor | null>(null);
    const [currentDeployedCompany, setCurrentDeployedCompany] = useState<Company | null>(null);
    const [loadingCurrentCompany, setLoadingCurrentCompany] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailRecipients, setEmailRecipients] = useState('');
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [showEmailPreview, setShowEmailPreview] = useState(false);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [emailBodyText, setEmailBodyText] = useState(''); // Plain text version for editing
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
    const [loading, setLoading] = useState(true);
    const [editingDeploymentDate, setEditingDeploymentDate] = useState<string | null>(null);
    const [editingDeploymentDateValue, setEditingDeploymentDateValue] = useState<string>('');
    const [updatingDeploymentDate, setUpdatingDeploymentDate] = useState<string | null>(null);
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
    const [companyTypes, setCompanyTypes] = useState<string[]>([]);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso'
    const isBankUser = userRole === 'bank';
    const pso = useAppSelector((s) => s.user.pso);
    const isRegionalHead = !!pso && /regional/i.test(pso.designation || '');
    const psoDesignation = pso?.designation as string | undefined;
    const isIncharge = psoDesignation && /incharge/i.test(psoDesignation);
    const canCreateRequest = !isIncharge && !isRegionalHead; // Only PSO and Bank users can create requests
    const isPSO = !isBankUser && !isIncharge && !isRegionalHead; // Regular PSO user

    useEffect(() => {
        if (!uid) return;

        const loadData = async () => {
            setLoading(true);
            try {
                const changeOfVendorsData = await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead, psoDesignation);
                console.log('Loaded change of vendor data:', changeOfVendorsData);
                setChangeOfVendors(changeOfVendorsData);
                setFiltered(changeOfVendorsData);

                // Extract unique company types
                const typesSet = new Set<string>();
                changeOfVendorsData.forEach(changeOfVendor => {
                    if (changeOfVendor.company?.companyType) {
                        typesSet.add(changeOfVendor.company.companyType);
                    }
                });
                setCompanyTypes(Array.from(typesSet));
            } catch (err: any) {
                console.error('Error loading change of vendor data:', err);
                showToast('Failed to load change of vendor data', 'error');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [uid, isBankUser, isRegionalHead, psoDesignation]);

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        let list = changeOfVendors;

        // Apply filter based on selected company type
        if (selectedFilter && selectedFilter !== 'All') {
            list = list.filter(changeOfVendor => changeOfVendor.company?.companyType === selectedFilter);
        }

        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(changeOfVendor => {
                const requestId = (changeOfVendor.requestId || '').toString().toLowerCase();
                const branchCode = (changeOfVendor.branch?.branchCode || '').toString().toLowerCase();
                const branchAddress = (changeOfVendor.branch?.address || '').toString().toLowerCase();
                const companyName = (changeOfVendor.company?.companyName || '').toString().toLowerCase();
                const companyType = (changeOfVendor.company?.companyType || '').toString().toLowerCase();
                const companyId = (changeOfVendor.company?.companyId || '').toString().toLowerCase();
                return requestId.includes(term) || branchCode.includes(term) || branchAddress.includes(term) ||
                    companyName.includes(term) || companyType.includes(term) || companyId.includes(term);
            });
        }

        setFiltered(list);
        setCurrentPage(1);
    }, [searchTerm, changeOfVendors, selectedFilter]);

    const handleReset = () => {
        setSearchTerm('');
        setSelectedFilter(null);
        setFiltered(changeOfVendors);
        setCurrentPage(1);
    };

    const fetchCurrentDeployedCompany = async (changeOfVendor: ChangeOfVendor) => {
        if (!uid || !changeOfVendor.branchId || !changeOfVendor.company) return;
        
        setLoadingCurrentCompany(true);
        try {
            const bankId = changeOfVendor.bankId || await fetchChangeOfVendorBankId(uid, isBankUser);
            if (!bankId) {
                setCurrentDeployedCompany(null);
                return;
            }

            // Get the company type from the new company
            const companyType = changeOfVendor.company.companyType;
            if (!companyType) {
                setCurrentDeployedCompany(null);
                return;
            }

            // Fetch all deployments for this branch
            const deploymentsSnap = await getDocs(collection(db, "banks", bankId, "companyDeployment"));
            const deployments = deploymentsSnap.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter((d: any) => d.branchId === changeOfVendor.branchId);

            // Fetch all companies
            const companies = await fetchCompaniesData(uid, isBankUser);

            // Find the currently deployed company of the same type
            for (const deployment of deployments) {
                const company = companies.find(c => c.id === (deployment as any).companiesId);
                if (company && company.companyType === companyType) {
                    setCurrentDeployedCompany(company);
                    return;
                }
            }

            setCurrentDeployedCompany(null);
        } catch (error) {
            console.error('Error fetching current deployed company:', error);
            setCurrentDeployedCompany(null);
        } finally {
            setLoadingCurrentCompany(false);
        }
    };

    const openViewModal = async (changeOfVendor: ChangeOfVendor) => {
        setSelectedChangeOfVendor(changeOfVendor);
        setShowViewModal(true);
        await fetchCurrentDeployedCompany(changeOfVendor);
    };

    const handleChangeOfVendorAdded = async () => {
        if (!uid) return;
        setLoading(true);
        try {
            const changeOfVendorsData = await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead);
            setChangeOfVendors(changeOfVendorsData);

            // Extract unique company types
            const typesSet = new Set<string>();
            changeOfVendorsData.forEach(cov => {
                if (cov.company?.companyType) {
                    typesSet.add(cov.company.companyType);
                }
            });
            setCompanyTypes(Array.from(typesSet));

            setCurrentPage(1);
            showToast('✅ Change of vendor request submitted successfully!', 'success');
            setShowAddModal(false);
        } catch (err: any) {
            console.error('Error refreshing change of vendors:', err);
            showToast('❌ Failed to refresh change of vendors', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (changeOfVendor: ChangeOfVendor) => {
        if (!uid || !changeOfVendor.bankId) return;
        setLoading(true);
        try {
            const bankId = changeOfVendor.bankId || await fetchChangeOfVendorBankId(uid, isBankUser);
            if (!bankId) {
                showToast('❌ Bank ID not found', 'error');
                return;
            }
            await updateChangeOfVendorStatus(bankId, changeOfVendor.id, 'Approved');
            showToast('✅ Change of vendor request approved successfully!', 'success');
            
            // Refresh data
            const changeOfVendorsData = await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead);
            setChangeOfVendors(changeOfVendorsData);
            setFiltered(changeOfVendorsData);
            
            // Update selected change of vendor in modal
            const updated = changeOfVendorsData.find(cov => cov.id === changeOfVendor.id);
            if (updated) {
                setSelectedChangeOfVendor(updated);
            }
        } catch (err: any) {
            console.error('Error approving change of vendor:', err);
            showToast('❌ Failed to approve request', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async (changeOfVendor: ChangeOfVendor) => {
        if (!uid || !changeOfVendor.bankId) return;
        
        if (!window.confirm(`Are you sure you want to cancel request ${changeOfVendor.requestId || changeOfVendor.id}?`)) {
            return;
        }

        setLoading(true);
        try {
            const bankId = changeOfVendor.bankId || await fetchChangeOfVendorBankId(uid, isBankUser);
            if (!bankId) {
                showToast('❌ Bank ID not found', 'error');
                return;
            }
            // Update status to 'Cancelled' instead of deleting
            await updateChangeOfVendorStatus(bankId, changeOfVendor.id, 'Cancelled' as any);
            showToast('✅ Change of vendor request cancelled', 'success');
            
            // Refresh data
            const changeOfVendorsData = await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead);
            setChangeOfVendors(changeOfVendorsData);
            setFiltered(changeOfVendorsData);
            
            // Update selected change of vendor in modal
            const updated = changeOfVendorsData.find(cov => cov.id === changeOfVendor.id);
            if (updated) {
                setSelectedChangeOfVendor(updated);
            }
        } catch (err: any) {
            console.error('Error cancelling change of vendor:', err);
            showToast('❌ Failed to cancel request', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async (changeOfVendor: ChangeOfVendor) => {
        if (!uid || !changeOfVendor.bankId) return;
        setLoading(true);
        try {
            const bankId = changeOfVendor.bankId || await fetchChangeOfVendorBankId(uid, isBankUser);
            if (!bankId) {
                showToast('❌ Bank ID not found', 'error');
                return;
            }
            await updateChangeOfVendorStatus(bankId, changeOfVendor.id, 'Rejected');
            showToast('✅ Change of vendor request rejected', 'success');
            
            // Refresh data
            const changeOfVendorsData = await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead, psoDesignation);
            setChangeOfVendors(changeOfVendorsData);
            setFiltered(changeOfVendorsData);
            
            // Update selected change of vendor in modal
            const updated = changeOfVendorsData.find(cov => cov.id === changeOfVendor.id);
            if (updated) {
                setSelectedChangeOfVendor(updated);
            }
        } catch (err: any) {
            console.error('Error rejecting change of vendor:', err);
            showToast('❌ Failed to reject request', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateDeploymentDate = async (changeOfVendor: ChangeOfVendor, newDate: string) => {
        if (!newDate || !uid) {
            showToast('Please select a valid date', 'error');
            return;
        }

        setUpdatingDeploymentDate(changeOfVendor.id);
        try {
            const bankId = await fetchChangeOfVendorBankId(uid, isBankUser);
            if (!bankId) {
                showToast('❌ Bank ID not found', 'error');
                return;
            }

            const deploymentDateObj = new Date(newDate);
            await updateDeploymentDate(bankId, changeOfVendor.id, deploymentDateObj);
            showToast('✅ Deployment date updated successfully', 'success');
            
            // Refresh data
            const designation: string | undefined = psoDesignation;
            const changeOfVendorsData = designation 
                ? await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead, designation)
                : await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead);
            setChangeOfVendors(changeOfVendorsData);
            setFiltered(changeOfVendorsData);
            
            // Update selected item if it's the one being updated
            if (selectedChangeOfVendor?.id === changeOfVendor.id) {
                const updated = changeOfVendorsData.find(cov => cov.id === changeOfVendor.id);
                if (updated) {
                    setSelectedChangeOfVendor(updated);
                }
            }
            
            setEditingDeploymentDate(null);
            setEditingDeploymentDateValue('');
        } catch (err: any) {
            console.error('Error updating deployment date:', err);
            showToast('❌ Failed to update deployment date', 'error');
        } finally {
            setUpdatingDeploymentDate(null);
        }
    };

    const formatDateForInput = (date: any): string => {
        if (!date) return '';
        const dateObj = date?.toDate ? date.toDate() : new Date(date);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper function to extract text content from HTML
    const stripHtmlTags = (html: string): string => {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    // Helper function to convert plain text to simple HTML
    const textToHtml = (text: string): string => {
        // Split by double newlines to create paragraphs
        const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim());
        const htmlParagraphs = paragraphs.map(p => {
            // Replace single newlines with <br>
            const withBreaks = p.trim().split('\n').join('<br>');
            return `<p style="margin-bottom: 15px;">${withBreaks}</p>`;
        });
        return `<div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto; padding: 20px; line-height: 1.8;">${htmlParagraphs.join('')}</div>`;
    };

    const formatChangeOfVendorEmailBody = async (changeOfVendor: ChangeOfVendor, currentCompany: Company | null) => {
        if (!changeOfVendor.company || !changeOfVendor.branch) {
            return '<p>Incomplete request information</p>';
        }

        const companyType = changeOfVendor.company.companyType || 'System';
        const branchCode = changeOfVendor.branch.branchCode || 'N/A';
        const branchAddress = changeOfVendor.branch.address || 'N/A';
        const companyName = changeOfVendor.company.companyName || 'N/A';
        const deploymentDate = changeOfVendor.deploymentDate 
            ? (() => {
                const dateObj = changeOfVendor.deploymentDate?.toDate 
                    ? changeOfVendor.deploymentDate.toDate() 
                    : new Date(changeOfVendor.deploymentDate);
                const year = dateObj.getFullYear();
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const day = String(dateObj.getDate()).padStart(2, '0');
                return `${day}-${month}-${year}`;
            })()
            : 'N/A';

        // Fetch company contact details
        let contactPersonName = 'Sir/Madam';
        let psoName = 'PSO';
        let psoRegion = '';
        
        try {
            if (changeOfVendor.bankId && changeOfVendor.company.id) {
                const companyDoc = await getDoc(doc(db, "banks", changeOfVendor.bankId, "companies", changeOfVendor.company.id));
                if (companyDoc.exists()) {
                    const companyData = companyDoc.data();
                    contactPersonName = companyData.contactPersonName || contactPersonName;
                }
            }
        } catch (err) {
            console.error('Error fetching company contact details:', err);
        }

        // Get PSO information from the branch's assigned PSO (not the current user)
        try {
            const branchPsoId = changeOfVendor.branch?.psoId;
            if (branchPsoId && changeOfVendor.bankId) {
                // Fetch PSO details from banks/{bankId}/pos/{psoId}
                const psoDoc = await getDoc(doc(db, "banks", changeOfVendor.bankId, "pos", branchPsoId));
                if (psoDoc.exists()) {
                    const psoData = psoDoc.data();
                    psoName = psoData.name || psoName;
                    
                    // Extract region from designation if available
                    const designation = psoData.designation || '';
                    if (designation.includes('North')) {
                        psoRegion = 'North Region';
                    } else if (designation.includes('South')) {
                        psoRegion = 'South Region';
                    } else if (designation.includes('East')) {
                        psoRegion = 'East Region';
                    } else if (designation.includes('West')) {
                        psoRegion = 'West Region';
                    } else if (designation.includes('Central')) {
                        psoRegion = 'Central Region';
                    } else {
                        psoRegion = designation || 'Region';
                    }
                }
            } else if (changeOfVendor.branch?.psoName) {
                // Fallback: use psoName from branch if available (from enriched branch data)
                psoName = changeOfVendor.branch.psoName;
                psoRegion = 'Region'; // Default if we can't determine region
            }
        } catch (err) {
            console.error('Error fetching branch PSO details:', err);
            // Fallback to branch psoName if available
            if (changeOfVendor.branch?.psoName) {
                psoName = changeOfVendor.branch.psoName;
                psoRegion = 'Region';
            }
        }

        // Determine system type text based on company type
        let systemType = '';
        let equipmentText = '';
        if (companyType === 'BAMS') {
            systemType = 'BAMS System';
            equipmentText = 'BAMS equipment';
        } else if (companyType === 'CCTV') {
            systemType = 'CCTV System';
            equipmentText = 'CCTV equipment';
        } else if (companyType === 'Guard Company' || companyType === 'Guarding') {
            systemType = 'Guarding Services';
            equipmentText = 'guarding services';
        } else {
            systemType = `${companyType} System`;
            equipmentText = `${companyType} equipment`;
        }

        // Format contact person name - handle cases where name might have "Mr." prefix
        let formattedContactName = contactPersonName;
        if (formattedContactName && !formattedContactName.toLowerCase().startsWith('mr.')) {
            formattedContactName = `Mr. ${formattedContactName}`;
        }

        // Format PSO name - handle cases where name might have "Mr." prefix
        let formattedPsoName = psoName;
        if (formattedPsoName && !formattedPsoName.toLowerCase().startsWith('mr.')) {
            formattedPsoName = `Mr. ${formattedPsoName}`;
        }

        return `
            <div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto; padding: 20px; line-height: 1.8;">
                <p style="margin-bottom: 20px;">Dear ${formattedContactName}, M/S ${companyName}</p>
                
                <p style="margin-bottom: 15px;">
                    In the light of existing SLA, please install New ${systemType} (Free Package) at HBL Branch Code ${branchCode} ${branchAddress} by ${deploymentDate}
                </p>
                
                <p style="margin-bottom: 20px;">
                    Dear ${formattedPsoName}, PSO ${psoRegion}
                </p>
                
                <p style="margin-bottom: 20px;">
                    Please coordinate with branch and vendor for installation of all ${equipmentText} as per SLA by M/S ${companyName}. In case of requirement of any additional equipment, kindly get approval from Head Security for further work processing.
                </p>
                
                <p style="margin-top: 30px; font-style: italic; color: #666;">
                    <strong>Note:</strong> "HBL reserves rights to cancel the subject Work Order if the installation is not completed within the stipulated time. The branch may be allocated to other vendor in case of non-compliance of SLA"
                </p>
            </div>
        `;
    };

    const handlePreviewEmail = async () => {
        if (!selectedChangeOfVendor) return;
        if (!emailRecipients.trim()) {
            showToast('Please enter at least one email address', 'warning');
            return;
        }

        // Validate email addresses
        const emailValidation = validateEmails(emailRecipients);
        if (!emailValidation.isValid) {
            showToast(`Invalid email addresses: ${emailValidation.invalid.join(', ')}`, 'error');
            return;
        }

        setIsGeneratingPreview(true);
        try {
            // Generate email body
            const generatedBody = await formatChangeOfVendorEmailBody(selectedChangeOfVendor, currentDeployedCompany);
            
            // Generate email subject based on company type
            const companyType = selectedChangeOfVendor.company?.companyType || 'System';
            let systemType = '';
            if (companyType === 'BAMS') {
                systemType = 'BAMS System';
            } else if (companyType === 'CCTV') {
                systemType = 'CCTV System';
            } else if (companyType === 'Guard Company' || companyType === 'Guarding') {
                systemType = 'Guarding Services';
            } else {
                systemType = `${companyType} System`;
            }
            
            const branchCode = selectedChangeOfVendor.branch?.branchCode || 'N/A';
            const branchAddress = selectedChangeOfVendor.branch?.address || 'N/A';
            const generatedSubject = `Work Order: Installation of New ${systemType} at HBL Branch Code ${branchCode} ${branchAddress}`;
            
            // Set the generated subject and body
            setEmailSubject(generatedSubject);
            setEmailBody(generatedBody);
            
            // Extract plain text from HTML for editing
            const plainText = stripHtmlTags(generatedBody);
            setEmailBodyText(plainText);
            
            // Show preview modal
            setShowEmailPreview(true);
        } catch (error: any) {
            console.error('Error generating email preview:', error);
            showToast(`Failed to generate email preview: ${error.message || 'Unknown error'}`, 'error');
        } finally {
            setIsGeneratingPreview(false);
        }
    };

    const handleSendEmail = async () => {
        if (!selectedChangeOfVendor) return;
        if (!emailRecipients.trim()) {
            showToast('Please enter at least one email address', 'warning');
            return;
        }

        // Validate email addresses
        const emailValidation = validateEmails(emailRecipients);
        if (!emailValidation.isValid) {
            showToast(`Invalid email addresses: ${emailValidation.invalid.join(', ')}`, 'error');
            return;
        }

        if (!emailSubject.trim() || !emailBodyText.trim()) {
            showToast('Please ensure email subject and body are not empty', 'warning');
            return;
        }

        setIsSendingEmail(true);
        try {
            // Convert plain text back to HTML
            const htmlBody = textToHtml(emailBodyText);
            
            const emailData = {
                to: emailValidation.valid,
                subject: emailSubject,
                body: htmlBody,
                isHtml: true
            };

            const results = await sendEmailsWithErrorHandling(emailData);

            // Prepare result message
            let resultMessage = "";
            if (results.successful.length > 0) {
                resultMessage += `✅ Successfully sent to ${results.successful.length} recipient(s): ${results.successful.map(r => r.email).join(', ')}`;
            }

            if (results.failed.length > 0) {
                if (resultMessage) resultMessage += "\n\n";
                resultMessage += `❌ Failed to send to ${results.failed.length} recipient(s):\n`;
                results.failed.forEach(failure => {
                    resultMessage += `• ${failure.email}: ${failure.error}\n`;
                });
            }

            showToast(resultMessage, results.failed.length === 0 ? 'success' : 'warning');

            if (results.failed.length === 0) {
                setShowEmailModal(false);
                setShowEmailPreview(false);
                setEmailRecipients('');
                setEmailSubject('');
                setEmailBody('');
                setEmailBodyText('');
            }
        } catch (error: any) {
            console.error('Error sending email:', error);
            showToast(`Failed to send email: ${error.message || 'Unknown error'}`, 'error');
        } finally {
            setIsSendingEmail(false);
        }
    };

    const totalPages = Math.ceil(filtered.length / perPage);
    const paginatedData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

    const exportToExcel = () => {
        if (filtered.length === 0) {
            setToast({ show: true, message: 'No change of vendor requests to export', type: 'warning' });
            return;
        }
        const worksheetData = filtered.map(item => ({
            'Request ID': item.requestId || item.id,
            'Branch Code': item.branch?.branchCode || 'N/A',
            'Branch Address': item.branch?.address || 'N/A',
            'Company Name': item.company?.companyName || 'N/A',
            'Company Type': item.company?.companyType || 'N/A',
            'Status': item.status || 'Pending',
            'Deployment Date': item.deploymentDate
                ? (item.deploymentDate as any)?.toDate
                    ? (item.deploymentDate as any).toDate().toLocaleDateString()
                    : new Date(item.deploymentDate as any).toLocaleDateString()
                : 'N/A',
            'Approval Date': (item as any).approvalDate
                ? (item as any).approvalDate?.toDate
                    ? (item as any).approvalDate.toDate().toLocaleDateString()
                    : new Date((item as any).approvalDate).toLocaleDateString()
                : 'N/A',
            'Reject Date': (item as any).rejectDate
                ? (item as any).rejectDate?.toDate
                    ? (item as any).rejectDate.toDate().toLocaleDateString()
                    : new Date((item as any).rejectDate).toLocaleDateString()
                : 'N/A',
        }));
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'ChangeOfVendor');
        const fileName = `Change_Of_Vendor_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        setToast({ show: true, message: 'Excel exported successfully', type: 'success' });
    };

    const exportToPdf = () => {
        if (filtered.length === 0) {
            setToast({ show: true, message: 'No change of vendor requests to export', type: 'warning' });
            return;
        }
        const docPdf = new jsPDF('l', 'pt', 'a4');
        docPdf.setFontSize(14);
        docPdf.text('Change of Vendor Requests', 40, 40);

        const rows = filtered.map(item => [
            item.requestId || item.id,
            item.branch?.branchCode || 'N/A',
            item.branch?.address || 'N/A',
            item.company?.companyName || 'N/A',
            item.company?.companyType || 'N/A',
            item.status || 'Pending',
            item.deploymentDate
                ? (item.deploymentDate as any)?.toDate
                    ? (item.deploymentDate as any).toDate().toLocaleDateString()
                    : new Date(item.deploymentDate as any).toLocaleDateString()
                : 'N/A',
        ]);

        autoTable(docPdf, {
            startY: 60,
            head: [[
                'Request ID',
                'Branch Code',
                'Branch Address',
                'Company Name',
                'Company Type',
                'Status',
                'Deployment Date',
            ]],
            body: rows,
            styles: { fontSize: 8, cellPadding: 4 },
            headStyles: { fillColor: [41, 98, 255] },
        });

        docPdf.save(`Change_Of_Vendor_${new Date().toISOString().split('T')[0]}.pdf`);
        setToast({ show: true, message: 'PDF exported successfully', type: 'success' });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Loading change of vendor requests...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-6">
                {/* Main Table Container */}
                <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-black/30">
                    {/* Header */}
                    <div className="border-b border-gray-200 p-6 dark:border-gray-700">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                    Change of vendor
                                </h1>
                                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                    Manage change of vendor requests
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <Button
                                    variant="secondary"
                                    onClick={exportToExcel}
                                    className="flex items-center gap-2"
                                >
                                    <Icon icon={FiDownload} className="h-4 w-4" />
                                    Export Excel
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={exportToPdf}
                                    className="flex items-center gap-2"
                                >
                                    <Icon icon={FiDownload} className="h-4 w-4" />
                                    Export PDF
                                </Button>
                                {canCreateRequest && (
                                    <Button
                                        onClick={() => setShowAddModal(true)}
                                        className="flex items-center gap-2"
                                    >
                                        <Icon icon={FiPlus} className="h-4 w-4" />
                                        Request
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="border-b border-gray-200 p-6 dark:border-gray-700">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                            <div className="relative flex-1">
                                <Icon
                                    icon={FiSearch}
                                    className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    type="text"
                                    placeholder="Search by branch code, address, company name, or company ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                />
                            </div>
                            <div className="relative">
                                <select
                                    value={selectedFilter || 'All'}
                                    onChange={(e) => setSelectedFilter(e.target.value === 'All' ? null : e.target.value)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-4 pr-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                >
                                    <option value="All">All Types</option>
                                    {companyTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {searchTerm && (
                                <Button
                                    variant="secondary"
                                    onClick={handleReset}
                                    className="text-sm whitespace-nowrap"
                                >
                                    Clear Search
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto rounded-b-xl">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-300 bg-gradient-to-r from-gray-50 to-gray-100 dark:border-gray-600 dark:from-gray-800/50 dark:to-gray-800/30">
                                    <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300" style={{ width: '12%' }}>
                                        Request ID
                                    </th>
                                    <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300" style={{ width: '12%' }}>
                                        Branch Code
                                    </th>
                                    <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300" style={{ width: '20%' }}>
                                        Branch Address
                                    </th>
                                    <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300" style={{ width: '18%' }}>
                                        Company Name
                                    </th>
                                    <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300" style={{ width: '12%' }}>
                                        Company Type
                                    </th>
                                    <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300" style={{ width: '10%' }}>
                                        Status
                                    </th>
                                    <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300" style={{ width: '12%' }}>
                                        Deployment Date
                                    </th>
                                    <th className="border-r border-gray-200 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300" style={{ width: '12%' }}>
                                        Approval/Reject Date
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300" style={{ width: '10%' }}>
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedData.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="border-r border-gray-200 px-6 py-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400 bg-white dark:bg-black/30">
                                            {searchTerm ? 'No change of vendor requests found matching your search' : 'No change of vendor requests available'}
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedData.map((changeOfVendor, index) => (
                                        <tr
                                            key={changeOfVendor.id}
                                            className={`transition-colors ${index % 2 === 0
                                                ? 'bg-white dark:bg-black/30'
                                                : 'bg-gray-50 dark:bg-gray-800/20'
                                                } hover:bg-blue-50 dark:hover:bg-blue-900/10 border-b border-gray-200 dark:border-gray-700 last:border-b-0`}
                                        >
                                            <td className="border-r border-gray-200 px-6 py-4 text-sm text-gray-900 dark:text-gray-100 dark:border-gray-700">
                                                {changeOfVendor.requestId || 'N/A'}
                                            </td>
                                            <td className="border-r border-gray-200 px-6 py-4 text-sm text-gray-900 dark:text-gray-100 dark:border-gray-700">
                                                {changeOfVendor.branch?.branchCode || 'N/A'}
                                            </td>
                                            <td className="border-r border-gray-200 px-6 py-4 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-400" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                {changeOfVendor.branch?.address || 'N/A'}
                                            </td>
                                            <td className="border-r border-gray-200 px-6 py-4 text-sm font-medium text-gray-900 dark:border-gray-700 dark:text-gray-100" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                <strong>{changeOfVendor.company?.companyName || 'N/A'}</strong>
                                            </td>
                                            <td className="border-r border-gray-200 px-6 py-4 text-sm text-gray-900 dark:text-gray-100 dark:border-gray-700">
                                                {changeOfVendor.company?.companyType || 'N/A'}
                                            </td>
                                            <td className="border-r border-gray-200 px-6 py-4 dark:border-gray-700">
                                                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                                    changeOfVendor.status === 'Approved' 
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                        : changeOfVendor.status === 'Rejected'
                                                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                                }`}>
                                                    {changeOfVendor.status || 'Pending'}
                                                </span>
                                            </td>
                                            <td className="border-r border-gray-200 px-6 py-4 text-sm text-gray-900 dark:text-gray-100 dark:border-gray-700">
                                                {editingDeploymentDate === changeOfVendor.id ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="date"
                                                            value={editingDeploymentDateValue}
                                                            onChange={(e) => setEditingDeploymentDateValue(e.target.value)}
                                                            className="rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                                                            min={changeOfVendor.requestDate ? formatDateForInput(changeOfVendor.requestDate) : undefined}
                                                            disabled={updatingDeploymentDate === changeOfVendor.id}
                                                        />
                                                        <button
                                                            onClick={() => handleUpdateDeploymentDate(changeOfVendor, editingDeploymentDateValue)}
                                                            disabled={updatingDeploymentDate === changeOfVendor.id}
                                                            className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700 disabled:opacity-50"
                                                        >
                                                            {updatingDeploymentDate === changeOfVendor.id ? '...' : '✓'}
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setEditingDeploymentDate(null);
                                                                setEditingDeploymentDateValue('');
                                                            }}
                                                            disabled={updatingDeploymentDate === changeOfVendor.id}
                                                            className="rounded bg-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-400 disabled:opacity-50 dark:bg-gray-600 dark:text-gray-100"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div 
                                                        className={`${isPSO ? 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400' : ''}`}
                                                        onClick={() => {
                                                            if (isPSO) {
                                                                setEditingDeploymentDate(changeOfVendor.id);
                                                                setEditingDeploymentDateValue(formatDateForInput(changeOfVendor.deploymentDate));
                                                            }
                                                        }}
                                                        title={isPSO ? 'Click to edit deployment date' : ''}
                                                    >
                                                        {changeOfVendor.deploymentDate ? (
                                                            changeOfVendor.deploymentDate?.toDate ? 
                                                                changeOfVendor.deploymentDate.toDate().toLocaleDateString() : 
                                                                new Date(changeOfVendor.deploymentDate).toLocaleDateString()
                                                        ) : (
                                                            <span className="text-gray-400 dark:text-gray-500">-</span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="border-r border-gray-200 px-6 py-4 text-sm text-gray-900 dark:text-gray-100 dark:border-gray-700">
                                                {changeOfVendor.status === 'Approved' && changeOfVendor.approvalDate ? (
                                                    <span className="text-green-700 dark:text-green-300">
                                                        {changeOfVendor.approvalDate?.toDate ? 
                                                            changeOfVendor.approvalDate.toDate().toLocaleDateString() : 
                                                            new Date(changeOfVendor.approvalDate).toLocaleDateString()}
                                                    </span>
                                                ) : changeOfVendor.status === 'Rejected' && changeOfVendor.rejectDate ? (
                                                    <span className="text-red-700 dark:text-red-300">
                                                        {changeOfVendor.rejectDate?.toDate ? 
                                                            changeOfVendor.rejectDate.toDate().toLocaleDateString() : 
                                                            new Date(changeOfVendor.rejectDate).toLocaleDateString()}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 dark:text-gray-500">-</span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <button
                                                    onClick={() => openViewModal(changeOfVendor)}
                                                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                                                    aria-label="View change of vendor"
                                                >
                                                    <Icon icon={FiEye} className="h-4 w-4 mr-1" />
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="border-t border-gray-200 p-6 dark:border-gray-700">
                            <div className="flex items-center justify-center gap-2 sm:gap-4">
                                <Button
                                    variant="secondary"
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className="text-sm"
                                >
                                    First
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className="text-sm"
                                >
                                    Previous
                                </Button>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 px-2">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <Button
                                    variant="secondary"
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className="text-sm"
                                >
                                    Next
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className="text-sm"
                                >
                                    Last
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Toast */}
            {toast.show && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToast({ ...toast, show: false })}
                        />
                    </div>
                </div>
            )}

            {/* Modals */}
            <AddChangeOfVendorModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleChangeOfVendorAdded}
            />

            {/* View Modal */}
            <Modal
                isOpen={showViewModal}
                onClose={() => {
                    setShowViewModal(false);
                    setSelectedChangeOfVendor(null);
                    setCurrentDeployedCompany(null);
                }}
                title="Change of Vendor Details"
                size="lg"
            >
                {selectedChangeOfVendor && (
                    <div className="p-6">
                        {/* Action Buttons at Top */}
                        <div className="mb-6 flex justify-end gap-3 pb-4 border-b border-gray-200 dark:border-gray-700">
                            {/* Email Button - Show for Incharge users */}
                            {isIncharge && (
                                <Button
                                    variant="secondary"
                                    onClick={() => setShowEmailModal(true)}
                                    disabled={loading}
                                    className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                                >
                                    <Icon icon={FiMail} className="h-4 w-4" />
                                    Send Email
                                </Button>
                            )}
                            {/* Cancel Button - Show for Pending requests */}
                            {selectedChangeOfVendor.status === 'Pending' && (
                                <Button
                                    variant="secondary"
                                    onClick={() => handleCancel(selectedChangeOfVendor)}
                                    disabled={loading}
                                    className="flex items-center gap-2 bg-orange-50 hover:bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300"
                                >
                                    <Icon icon={FiX} className="h-4 w-4" />
                                    Cancel
                                </Button>
                            )}
                            {/* Approve/Reject Buttons for Regional Head - Only for Pending */}
                            {selectedChangeOfVendor.status === 'Pending' && (
                                <>
                                    {isRegionalHead && (
                                        <>
                                            <Button
                                                variant="secondary"
                                                onClick={() => handleReject(selectedChangeOfVendor)}
                                                disabled={loading}
                                                className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                                            >
                                                <Icon icon={FiX} className="h-4 w-4" />
                                                Reject
                                            </Button>
                                            <Button
                                                onClick={() => handleApprove(selectedChangeOfVendor)}
                                                disabled={loading}
                                                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
                                            >
                                                <Icon icon={FiCheck} className="h-4 w-4" />
                                                Approve
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request ID</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {selectedChangeOfVendor.requestId || 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                                    <p className="mt-1">
                                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                            selectedChangeOfVendor.status === 'Approved' 
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                : selectedChangeOfVendor.status === 'Rejected'
                                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                : selectedChangeOfVendor.status === 'Cancelled'
                                                ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
                                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                        }`}>
                                            {selectedChangeOfVendor.status || 'Pending'}
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {selectedChangeOfVendor.branch?.branchCode || 'N/A'}
                                    </p>
                                </div>
                                <div className="col-span-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Address</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {selectedChangeOfVendor.branch?.address || 'N/A'}
                                    </p>
                                </div>
                                <div className="col-span-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2 block">Company Type</label>
                                    <p className="text-sm text-gray-900 dark:text-gray-100">
                                        {selectedChangeOfVendor.company?.companyType || 'N/A'}
                                    </p>
                                </div>
                                
                                {/* Current Deployed Company */}
                                <div className="col-span-2">
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 block">
                                            Current Deployed Company
                                        </label>
                                        {loadingCurrentCompany ? (
                                            <div className="text-sm text-gray-500 dark:text-gray-400">Loading...</div>
                                        ) : currentDeployedCompany ? (
                                            <div className="space-y-2">
                                                <div>
                                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Company Name:</span>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                        {currentDeployedCompany.companyName || 'N/A'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Company ID:</span>
                                                    <p className="text-sm text-gray-900 dark:text-gray-100">
                                                        {currentDeployedCompany.companyId || 'N/A'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Company Type:</span>
                                                    <p className="text-sm text-gray-900 dark:text-gray-100">
                                                        {currentDeployedCompany.companyType || 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                                                No company currently deployed for this type
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* New Requested Company */}
                                <div className="col-span-2">
                                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                                        <label className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3 block">
                                            New Requested Company
                                        </label>
                                        {selectedChangeOfVendor.company ? (
                                            <div className="space-y-2">
                                                <div>
                                                    <span className="text-xs font-medium text-blue-800 dark:text-blue-200">Company Name:</span>
                                                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                                        {selectedChangeOfVendor.company.companyName || 'N/A'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-xs font-medium text-blue-800 dark:text-blue-200">Company ID:</span>
                                                    <p className="text-sm text-blue-900 dark:text-blue-100">
                                                        {selectedChangeOfVendor.company.companyId || 'N/A'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-xs font-medium text-blue-800 dark:text-blue-200">Company Type:</span>
                                                    <p className="text-sm text-blue-900 dark:text-blue-100">
                                                        {selectedChangeOfVendor.company.companyType || 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-sm text-blue-700 dark:text-blue-300 italic">
                                                Company information not available
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {selectedChangeOfVendor.requestDate && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request Date</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            {selectedChangeOfVendor.requestDate?.toDate ? 
                                                selectedChangeOfVendor.requestDate.toDate().toLocaleDateString() : 
                                                new Date(selectedChangeOfVendor.requestDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date of Deployment</label>
                                    {editingDeploymentDate === selectedChangeOfVendor.id ? (
                                        <div className="mt-2 flex items-center gap-2">
                                            <TextField
                                                type="date"
                                                value={editingDeploymentDateValue}
                                                onChange={(e) => setEditingDeploymentDateValue(e.target.value)}
                                                min={selectedChangeOfVendor.requestDate ? formatDateForInput(selectedChangeOfVendor.requestDate) : undefined}
                                                disabled={updatingDeploymentDate === selectedChangeOfVendor.id}
                                                className="w-48"
                                            />
                                            <Button
                                                onClick={() => handleUpdateDeploymentDate(selectedChangeOfVendor, editingDeploymentDateValue)}
                                                disabled={updatingDeploymentDate === selectedChangeOfVendor.id || !editingDeploymentDateValue}
                                                className="px-3 py-1 text-xs"
                                            >
                                                {updatingDeploymentDate === selectedChangeOfVendor.id ? 'Updating...' : 'Save'}
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                onClick={() => {
                                                    setEditingDeploymentDate(null);
                                                    setEditingDeploymentDateValue('');
                                                }}
                                                disabled={updatingDeploymentDate === selectedChangeOfVendor.id}
                                                className="px-3 py-1 text-xs"
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="mt-1 flex items-center gap-2">
                                            <p className="text-sm text-gray-900 dark:text-gray-100">
                                                {selectedChangeOfVendor.deploymentDate ? (
                                                    selectedChangeOfVendor.deploymentDate?.toDate ? 
                                                        selectedChangeOfVendor.deploymentDate.toDate().toLocaleDateString() : 
                                                        new Date(selectedChangeOfVendor.deploymentDate).toLocaleDateString()
                                                ) : (
                                                    <span className="text-gray-400 dark:text-gray-500">Not set</span>
                                                )}
                                            </p>
                                            {isPSO && (
                                                <Button
                                                    variant="secondary"
                                                    onClick={() => {
                                                        setEditingDeploymentDate(selectedChangeOfVendor.id);
                                                        setEditingDeploymentDateValue(formatDateForInput(selectedChangeOfVendor.deploymentDate));
                                                    }}
                                                    className="px-2 py-1 text-xs"
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {selectedChangeOfVendor.approvalDate && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Approval Date</label>
                                        <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                                            {selectedChangeOfVendor.approvalDate?.toDate ? 
                                                selectedChangeOfVendor.approvalDate.toDate().toLocaleDateString() : 
                                                new Date(selectedChangeOfVendor.approvalDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                )}
                                {selectedChangeOfVendor.rejectDate && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Reject Date</label>
                                        <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                                            {selectedChangeOfVendor.rejectDate?.toDate ? 
                                                selectedChangeOfVendor.rejectDate.toDate().toLocaleDateString() : 
                                                new Date(selectedChangeOfVendor.rejectDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                )}
                                {selectedChangeOfVendor.createdAt && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Created At</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            {selectedChangeOfVendor.createdAt?.toDate ? 
                                                selectedChangeOfVendor.createdAt.toDate().toLocaleString() : 
                                                new Date(selectedChangeOfVendor.createdAt).toLocaleString()}
                                        </p>
                                    </div>
                                )}
                                {selectedChangeOfVendor.updatedAt && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Updated At</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            {selectedChangeOfVendor.updatedAt?.toDate ? 
                                                selectedChangeOfVendor.updatedAt.toDate().toLocaleString() : 
                                                new Date(selectedChangeOfVendor.updatedAt).toLocaleString()}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Email Modal */}
            <Modal
                isOpen={showEmailModal}
                onClose={() => {
                    setShowEmailModal(false);
                    setEmailRecipients('');
                }}
                title="Send Email - Change of Vendor Request"
                size="md"
            >
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            Email Recipients (comma-separated)
                        </label>
                        <TextField
                            type="text"
                            placeholder="email1@example.com, email2@example.com"
                            value={emailRecipients}
                            onChange={(e) => setEmailRecipients(e.target.value)}
                            disabled={isSendingEmail}
                            className="w-full"
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Enter one or more email addresses separated by commas
                        </p>
                    </div>

                    {isGeneratingPreview && (
                        <div className="flex items-center justify-center py-4">
                            <div className="text-center">
                                <div className="mb-2 inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Generating email preview...</p>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowEmailModal(false);
                                setEmailRecipients('');
                            }}
                            disabled={isSendingEmail}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handlePreviewEmail}
                            disabled={isGeneratingPreview || !emailRecipients.trim()}
                            className="flex items-center gap-2"
                        >
                            <Icon icon={FiMail} className="h-4 w-4" />
                            {isGeneratingPreview ? 'Generating Preview...' : 'Preview & Send'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Email Preview Modal */}
            <Modal
                isOpen={showEmailPreview}
                onClose={() => {
                    setShowEmailPreview(false);
                }}
                title="Email Preview - Edit Before Sending"
                size="lg"
            >
                <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            Email Recipients
                        </label>
                        <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300">
                            {emailRecipients}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            Subject
                        </label>
                        <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300">
                            {emailSubject}
                        </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            Email Preview (How it will appear)
                        </label>
                        <div 
                            className="bg-white dark:bg-gray-900 rounded border border-gray-300 dark:border-gray-600 p-4 max-h-[500px] overflow-y-auto"
                            dangerouslySetInnerHTML={{ __html: emailBody }}
                        />
                    </div>

                    {isSendingEmail && (
                        <div className="flex items-center justify-center py-4">
                            <div className="text-center">
                                <div className="mb-2 inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Sending email...</p>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowEmailPreview(false);
                            }}
                            disabled={isSendingEmail}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSendEmail}
                            disabled={isSendingEmail || !emailSubject.trim() || !emailBodyText.trim()}
                            className="flex items-center gap-2"
                        >
                            <Icon icon={FiMail} className="h-4 w-4" />
                            {isSendingEmail ? 'Sending...' : 'Send Email'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}

